import React, { useState } from 'react';
import { Button, Card, Col, Row, Select, Space, Statistic } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/store/authStore';
import { useCrmActivities, useCrmActivityTypes } from '@/hooks/useCrmActivities';
import CrmActivityTimeline from './CrmActivityTimeline';
import CrmActivityForm from './CrmActivityForm';
import { formatDate } from '@/utils/format/date';

interface Props {
  customerId: string;
  summary?: {
    last_contact_at: string | null;
    open_follow_ups: number;
    overdue_follow_ups: number;
    activities_90d: number;
  };
}

/** Customer Interaction History: every activity on the customer, its leads and its opportunities. */
const CustomerInteractionsPanel: React.FC<Props> = ({ customerId, summary }) => {
  const { user } = useAuthStore();
  const canLog = Boolean(user?.is_super_admin || user?.permissions?.includes('crm.activity.create'));
  const [showForm, setShowForm] = useState(false);
  const [typeCodes, setTypeCodes] = useState<string[]>([]);
  const { data: types = [] } = useCrmActivityTypes();
  const { data, isLoading } = useCrmActivities({
    customer_id: customerId,
    include_system: true,
    activity_type_codes: typeCodes.length ? typeCodes.join(',') : undefined,
    limit: 200,
  });

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Last customer contact"
              value={summary?.last_contact_at ? formatDate(summary.last_contact_at) : '—'}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Activities (90 days)" value={summary?.activities_90d ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Open follow-ups" value={summary?.open_follow_ups ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Overdue follow-ups"
              value={summary?.overdue_follow_ups ?? 0}
              valueStyle={summary?.overdue_follow_ups ? { color: '#cf1322' } : undefined}
            />
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        title="Interaction history"
        loading={isLoading}
        extra={
          <Space>
            <Select
              mode="multiple"
              allowClear
              placeholder="All types"
              style={{ minWidth: 200 }}
              value={typeCodes}
              onChange={setTypeCodes}
              options={types.map((t) => ({ value: t.code, label: t.name }))}
            />
            {canLog && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowForm((v) => !v)}>
                Log interaction
              </Button>
            )}
          </Space>
        }
      >
        {showForm && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <CrmActivityForm
              link={{ customer_id: customerId }}
              onSaved={() => setShowForm(false)}
              onCancel={() => setShowForm(false)}
            />
          </Card>
        )}
        <CrmActivityTimeline
          activities={data?.items ?? []}
          showRecordLinks
          emptyText="No interactions recorded for this customer yet"
        />
      </Card>
    </>
  );
};

export default CustomerInteractionsPanel;
